import { com } from '@eidu/form'
import { useContext, useEffect, useState } from 'react'
import { AuthContext, IAuthContext } from 'react-oauth2-code-pkce'
import EntityWithLabelAndRelated from '../domain/entity/EntityWithLabelAndRelated'
import useEntityRepository from './useEntityRepository'
import { setGlobalAuthContext } from './EntityRepository'
import { EntityTypesContext } from './context/EntityTypes'
import EntityTypeId = com.eidu.sharedlib.entity.type.EntityTypeId
import EntityId = com.eidu.sharedlib.entity.EntityId
import EntityType = com.eidu.sharedlib.entity.type.EntityType

type UseEntityByIdResult = { entity: EntityWithLabelAndRelated | null | undefined; loading: boolean }
const EMPTY_ENTITY_TYPES = new Map<EntityTypeId, EntityType>()

const useEntityById = (id: EntityId | undefined): UseEntityByIdResult => {
  const authContext = useContext<IAuthContext>(AuthContext)
  useEffect(() => {
    setGlobalAuthContext(authContext)
  }, [authContext])

  const entityTypesRepo = useContext(EntityTypesContext)
  const types = entityTypesRepo.useAll()
  const typesResolved = types !== undefined
  const authToken = authContext.token?.trim()
  const authOrganizationId = authContext.idTokenData?.organization_id?.trim()
  const authUserEntityId = authContext.idTokenData?.user_entity_id?.trim()
  const authReady = Boolean(authToken && authOrganizationId && authUserEntityId)
  const resolvedTypes = types ?? EMPTY_ENTITY_TYPES

  const repo = useEntityRepository({ typeId: undefined, types: resolvedTypes, pageSize: 1 }, [
    JSON.stringify([...resolvedTypes.keys()].map((it) => it.toString())),
  ])

  const [value, setValue] = useState<EntityWithLabelAndRelated | null | undefined>(undefined)
  const [loading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    let cancelled = false
    if (!id) {
      setValue(undefined)
      setLoading(false)
    } else if (!typesResolved) {
      setValue(undefined)
      setLoading(true)
    } else if (!authReady) {
      setValue(undefined)
      setLoading(Boolean(authContext.loginInProgress || !authContext.error))
    } else {
      setLoading(true)
      repo
        .get(id)
        .then((it) => {
          if (!cancelled) setValue(it)
        })
        .catch((error) => {
          if (cancelled) return
          const message = error instanceof Error ? error.message : String(error)
          if (message.includes('Authentication context is missing')) {
            setValue(undefined)
            return
          }
          setValue(null)
        })
        .finally(() => {
          if (!cancelled) setLoading(false)
        })
    }
    return () => {
      cancelled = true
    }
  }, [
    authContext.error,
    authContext.loginInProgress,
    authOrganizationId,
    authReady,
    authToken,
    authUserEntityId,
    id?.toString(),
    repo,
    typesResolved,
  ])

  return { entity: value, loading }
}

export default useEntityById
